import {
    Box,
    Breadcrumbs,
    Button,
    Divider,
    Grid2,
    IconButton,
    Link,
    Snackbar,
    Alert,
    Tab,
    Tabs,
    Typography,
    Badge,
} from '@mui/material';
import { useState, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import StarIcon from '@mui/icons-material/Star';
import StarHalfIcon from '@mui/icons-material/StarHalf';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import XIcon from '@mui/icons-material/X';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { Icon } from '@iconify/react/dist/iconify.js';
import { Page_Padding, ssoLoginUrl } from '../../shared/constants';
import { useSkillPreskoolProducts, useSkillBoxProductsDetails, useSkillProductCategory, useSkillBoxProducts } from '../../assets/api/useGetApi';
import SkillBoxProductCard from '../../shared/components/SkillBoxProductCard';
import CustomDivider from '../../shared/utils/CustomDivider';
import { useSkillBoxCart } from '../../assets/nodeApi/getApi/getCommonApi';
import { apiUrl } from '../../assets/nodeApi/url';
import { postCart } from '../../assets/nodeApi/postApi/postCommonApi';
import useVisitedPathStore from '../../shared/store/useVisitedPathStore';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const resolveImage = (image_small) => {
    if (!image_small) return '';
    if (image_small.startsWith('http://') || image_small.startsWith('https://')) return image_small;
    return `https://myskill.club/shop/uploads/images/${image_small}`;
};

const StarRating = ({ rating = 0, totalReviews }) => {
    const stars = Array.from({ length: 5 }, (_, i) => {
        const filled = i + 1 <= Math.floor(rating);
        const half = !filled && i < rating;
        return filled ? 'full' : half ? 'half' : 'empty';
    });
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
            {stars.map((type, i) =>
                type === 'full' ? (
                    <StarIcon key={i} sx={{ fontSize: '18px', color: '#F5A623' }} />
                ) : type === 'half' ? (
                    <StarHalfIcon key={i} sx={{ fontSize: '18px', color: '#F5A623' }} />
                ) : (
                    <StarBorderIcon key={i} sx={{ fontSize: '18px', color: '#F5A623' }} />
                )
            )}
            {rating !== undefined && (
                <Typography variant="caption" color="grey.500" sx={{ ml: 0.5 }}>
                    {rating} ratings
                </Typography>
            )}
        </Box>
    );
};

// ─── Tab Panel ───────────────────────────────────────────────────────────────

const TabPanel = ({ children, value, index }) => (
    <Box
        role="tabpanel"
        hidden={value !== index}
        id={`product-tab-panel-${index}`}
        aria-labelledby={`product-tab-${index}`}
        sx={{ pt: 2 }}
    >
        {value === index && children}
    </Box>
);


// ─── Main Component ───────────────────────────────────────────────────────────

const SkillBoxProductDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const queryClient = useQueryClient();

    const sourcePath = location.state?.sourcePath || '/SkillBox';
    const sourceName = location.state?.sourceName || 'Skill Box';

    // ── Product data ──────────────────────────────────────────────────────────
    const { data: detailData, isLoading: isDetailLoading } = useSkillBoxProductsDetails(id);
    const apiProduct = detailData?.data;

    const { data: skillBoxData, isLoading } = useSkillPreskoolProducts();
    const { data: categoriesData } = useSkillProductCategory();

    // Normalize API fields (title→course_name, image→image_small)
    const allProducts = (skillBoxData?.data || []).map((p) => ({
        ...p,
        course_name: p.title,
        image_small: p.image,
    }));

    // Find product by id/slug; fall back to a dummy if not yet loaded / not found
    const product = useMemo(() => {
        if (apiProduct) {
            return {
                ...apiProduct,
                course_name: apiProduct.title,
                image_small: apiProduct.images && apiProduct.images.length > 0 ? apiProduct.images[0] : apiProduct.image,
                price: apiProduct.price,
                price_discounted: apiProduct.price_discounted,
                rating: apiProduct.rating || 0,
                stock: apiProduct.stock,
                url: apiProduct.url,
                description: apiProduct.description,
                short_description: apiProduct.short_description,
            };
        }

        if (allProducts.length > 0) {
            const found = allProducts.find((p) => p?.slug === id || String(p?.id) === String(id));
            if (found) return found;
        }

        return null;
    }, [allProducts, id, apiProduct]);

    // ── Fetch Similar Products by Category ────────────────────────────────────
    const { data: similarProductsData } = useSkillBoxProducts(product?.category?.id);

    // ── Gallery images ────────────────────────────────────────────────────────
    const mainImage = resolveImage(product?.image_small);
    const galleryImages = apiProduct?.images?.length > 0
        ? apiProduct.images.map(resolveImage)
        : mainImage ? [mainImage] : [];
    const [selectedImg, setSelectedImg] = useState(0);

    // // ── Size selector ─────────────────────────────────────────────────────────
    // const sizes = ['24', '23', '22', '21', '20'];
    // const [selectedSize, setSelectedSize] = useState(null);

    // ── Quantity ──────────────────────────────────────────────────────────────
    const [qty, setQty] = useState(1);
    const handleQtyChange = (delta) => setQty((prev) => Math.max(1, prev + delta));

    // ── Tabs ──────────────────────────────────────────────────────────────────
    const [activeTab, setActiveTab] = useState(0);

    const { descriptionHTML, contentsList } = useMemo(() => {
        let desc = product?.description || '';
        let contents = [];

        if (desc) {
            try {
                const parser = new DOMParser();
                const doc = parser.parseFromString(desc, 'text/html');
                const tables = doc.querySelectorAll('table');
                if (tables.length > 0) {
                    const rows = tables[0].querySelectorAll('tr');
                    rows.forEach((row, rowIndex) => {
                        if (rowIndex > 0) {
                            const cells = row.querySelectorAll('td, th');
                            let material = '';
                            let qty = '';
                            if (cells.length >= 3) {
                                material = cells[1].textContent || '';
                                qty = cells[2].textContent || '';
                            } else if (cells.length === 2) {
                                material = cells[0].textContent || '';
                                qty = cells[1].textContent || '';
                            } else if (cells.length === 1) {
                                material = cells[0].textContent || '';
                            }

                            material = material.replace(/\u00A0/g, ' ').replace(/[\u200B-\u200D\uFEFF]/g, '').replace(/\s+/g, ' ').trim();
                            qty = qty.replace(/\u00A0/g, ' ').replace(/[\u200B-\u200D\uFEFF]/g, '').replace(/\s+/g, ' ').trim();

                            if (material && !material.toLowerCase().includes('material')) {
                                if (qty) {
                                    contents.push(`${material} (${qty})`);
                                } else {
                                    contents.push(material);
                                }
                            }
                        }
                    });
                }
            } catch (e) {
                console.error("Error parsing materials", e);
            }
        }

        return { descriptionHTML: desc, contentsList: contents };
    }, [product?.description, product?.category?.id]);

    // ── Cart store ────────────────────────────────────────────────────────────
    const { data: cartData } = useSkillBoxCart();
    const [snackbar, setSnackbar] = useState({ open: false, msg: '', severity: 'success', showAction: true });

    const isInCart = useMemo(() => {
        if (!product || !cartData?.data) return false;
        return cartData.data.some(c => String(c.product_id || c.productId || c.product?.id || c.id) === String(product.id));
    }, [cartData, product]);

    const checkAuth = () => {
        try {
            const authStorage = JSON.parse(localStorage.getItem('auth-storage'));
            return !!authStorage?.state?.accessToken;
        } catch (e) {
            console.error("Error reading auth storage", e);
            return false;
        }
    };

    const handleAddToCart = () => {
        if (!checkAuth()) {
            setSnackbar({ open: true, msg: 'Please login and buy products or add your favorite items to wishlist', severity: 'info', showAction: false });
            return;
        }
        postCart({ productId: product.id, quantity: qty })
            .then(() => queryClient.invalidateQueries({ queryKey: ["v1/cart"] }))
            .catch(err => console.error("Error adding to cart API", err));
        setSnackbar({ open: true, msg: `" ${product?.course_name} " added to cart !`, severity: 'success', showAction: true });
    };

    // Fix: Purchase Now should REPLACE qty in cart (not accumulate) to avoid qty=2 bug
    const handlePurchaseNow = () => {
        if (!checkAuth()) {
            setSnackbar({ open: true, msg: 'Please login and buy products or add your favorite items to wishlist', severity: 'info', showAction: false });
            useVisitedPathStore.getState().setLastVisitedPath(location.pathname);
            localStorage.setItem("lastVisitedPath", location.pathname);
            window.location.href = ssoLoginUrl;
            return;
        }
        postCart({ productId: product.id, quantity: qty })
            .then(() => queryClient.invalidateQueries({ queryKey: ["v1/cart"] }))
            .catch(err => console.error("Error adding to cart API", err));
        navigate('/Cartpage', { state: { sourcePath, sourceName } });
    };

    // ── Share helpers ─────────────────────────────────────────────────────────
    const productUrl = product?.url || `${apiUrl}${id}`;
    const shareText = encodeURIComponent(`Check out this product: ${product?.course_name}`);
    const whatsappUrl = `https://wa.me/?text=${shareText}%20${encodeURIComponent(productUrl)}`;
    const instagramUrl = `https://www.instagram.com/`; // Instagram does not support direct share URLs
    const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(productUrl)}`;
    const twitterUrl = `https://twitter.com/intent/tweet?text=${shareText}&url=${encodeURIComponent(productUrl)}`;

    // ── Similar products ──────────────────────────────────────────────────────
    const similarProducts = useMemo(() => {
        const list = similarProductsData?.data || [];
        return list
            .filter((p) => String(p?.id) !== String(product?.id) && p?.slug !== product?.slug)
            .map((p) => ({
                ...p,
                course_name: p.title,
                image_small: p.images && p.images.length > 0 ? p.images[0] : p.image,
            }))
            .slice(0, 4);
    }, [similarProductsData, product?.id, product?.slug]);

    return (
        <>
            <Box className="body" px={Page_Padding} sx={{ maxWidth: '1536px', mx: 'auto', pb: '4rem' }}>

                <Grid2 container spacing={2} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <Grid2 size={{ xs: 9, sm: 8, md: 8 }}>
                        {/* ── Breadcrumb ── */}
                        <Breadcrumbs
                            separator={<NavigateNextIcon fontSize="small" />}
                            aria-label="breadcrumb"
                            sx={{ py: '1.25rem' }}
                        >
                            <Link
                                underline="hover"
                                color="inherit"
                                onClick={() => navigate('/')}
                                sx={{ cursor: 'pointer', fontSize: '13px', color: "grey.800", fontWeight: 400 }}
                            >
                                Home
                            </Link>
                            {sourcePath && sourcePath !== '/' && (
                                <Link
                                    underline="hover"
                                    color="inherit"
                                    onClick={() => navigate(sourcePath)}
                                    sx={{ cursor: 'pointer', fontSize: '13px', color: "grey.800", fontWeight: 400 }}
                                >
                                    {sourceName}
                                </Link>

                            )}
                            <Typography color="text.primary" sx={{ fontSize: '13px' }}>Products</Typography>

                        </Breadcrumbs>
                    </Grid2>
                    <Grid2 size={{ xs: 3, sm: 4, md: 4 }}>
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: "1.2rem" }}>
                            <Icon icon="mingcute:heart-fill" width={24} height={24} color='red' style={{ cursor: "pointer" }} onClick={() => {
                                if (checkAuth()) {
                                    navigate('/Wishlist', { state: { sourcePath, sourceName } });
                                } else {
                                    setSnackbar({ open: true, msg: 'Please login and buy products or add your favorite items to wishlist', severity: 'info', showAction: false });
                                }
                            }} />
                            <Badge
                                badgeContent={checkAuth() ? (cartData?.data?.length || 0) : 0}
                                color="error"
                                sx={{
                                    '& .MuiBadge-badge': {
                                        bgcolor: 'red.main',
                                        color: '#fff',
                                        fontSize: '10px',
                                        fontWeight: 500,
                                        height: '16px',
                                        minWidth: '16px',
                                        padding: '0'
                                    }
                                }}
                            >
                                <Icon icon="ion:cart-outline" width={24} height={24} color='#777' style={{ cursor: "pointer" }} onClick={() => {
                                    if (checkAuth()) {
                                        navigate('/Cartpage', { state: { sourcePath, sourceName } });
                                    } else {
                                        setSnackbar({ open: true, msg: 'Please login and buy products or add your favorite items to wishlist', severity: 'info', showAction: false });
                                    }
                                }} />
                            </Badge>
                        </Box>
                    </Grid2>
                </Grid2>

                {/* ── Product Card ── */}
                <Box
                    sx={{
                        border: '1px solid #E7E7E7',
                        borderRadius: '12px',
                        p: { xs: '1rem', md: '1.5rem' },
                        mb: '1.5rem',
                    }}
                >
                    <Grid2 container spacing={{ xs: '1rem', md: '2rem' }}>

                        {/* ── LEFT: Image Gallery ── */}
                        <Grid2 size={{ xs: 12, md: 5 }}>
                            <Box sx={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start' }}>

                                {/* Main image */}
                                <Box
                                    sx={{
                                        flex: 1,
                                        bgcolor: '#f5f5f5',
                                        borderRadius: '12px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        minHeight: { xs: '200px', sm: '260px' },
                                        overflow: 'hidden',
                                        position: 'relative',
                                    }}
                                >
                                    {galleryImages.length > 0 && (
                                        <Box
                                            component="img"
                                            src={galleryImages[selectedImg]}
                                            alt={product?.course_name}
                                            sx={{
                                                width: '85%',
                                                height: '85%',
                                                objectFit: 'contain',
                                                transition: 'opacity 0.3s ease',
                                                borderRadius: "0px"
                                            }}
                                        />
                                    )}
                                </Box>

                                {/* Thumbnails - hidden on mobile */}
                                <Box sx={{ display: { xs: 'none', sm: 'flex' }, flexDirection: 'column', gap: '0.5rem' }}>
                                    {galleryImages.map((img, idx) => (
                                        <Box
                                            key={idx}
                                            onClick={() => setSelectedImg(idx)}
                                            sx={{
                                                width: '64px',
                                                height: '64px',
                                                borderRadius: '8px',
                                                overflow: 'hidden',
                                                border: selectedImg === idx ? '2px solid' : '2px solid transparent',
                                                borderColor: selectedImg === idx ? 'red.main' : 'transparent',
                                                bgcolor: '#f5f5f5',
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                transition: 'border-color 0.2s ease',
                                                '&:hover': { border: '2px solid', borderColor: 'red.main' },
                                            }}
                                        >
                                            <Box
                                                component="img"
                                                src={img}
                                                alt={`thumbnail-${idx}`}
                                                sx={{ width: '90%', height: '90%', objectFit: 'contain', borderRadius: "0px" }}
                                            />
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        </Grid2>

                        {/* ── RIGHT: Product Info ── */}
                        <Grid2 size={{ xs: 12, md: 7 }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0.65rem' }}>

                                {/* Name + Price row */}
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
                                    <Typography variant="h5" fontWeight={600} sx={{ lineHeight: 1.3 }}>
                                        {product?.course_name}
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0, mt: 0.5 }}>
                                        {product?.price !== product?.price_discounted ? (
                                            <Typography
                                                variant="body2"
                                                color="grey.400"
                                                sx={{ textDecoration: 'line-through', fontSize: '14px' }}
                                            >
                                                ₹ {product?.price}
                                            </Typography>
                                        ) : null}
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <CurrencyRupeeIcon sx={{ color: 'red.main', fontSize: '20px' }} />
                                            <Typography variant="h6" fontWeight={600} color="#000">
                                                {product?.price_discounted}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Box>

                                {/* Short description */}
                                <Typography variant="body2" color="grey.600">
                                    {product?.short_description}
                                </Typography>

                                {/* Stock status */}
                                <Box>
                                    <Typography variant="body2" sx={{ color: '#2E8B57', fontWeight: 600 }}>
                                        {product?.stock ? `${product.stock} Units Left!` : ''}
                                    </Typography>
                                    <Typography variant="caption" color="grey.500">
                                        Shipping free charge may vary according to location
                                    </Typography>
                                </Box>

                                {/* Rating */}
                                <StarRating rating={product?.rating} totalReviews={1} />

                                <Divider sx={{ my: '0.25rem' }} />

                                {/* Size */}
                                {/* <Box mt={0.5} mb={1}>
                                    <Typography variant="body2" fontWeight={600} mb={0.75}>
                                        Size
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                        {sizes.map((s) => (
                                            <Box
                                                key={s}
                                                onClick={() => setSelectedSize(s)}
                                                sx={{
                                                    width: '40px',
                                                    height: '40px',
                                                    borderRadius: '6px',
                                                    border: selectedSize === s ? '2px solid' : '1px solid',
                                                    borderColor: selectedSize === s ? 'red.main' : '#D9D9D9',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    cursor: 'pointer',
                                                    fontSize: '13px',
                                                    fontWeight: selectedSize === s ? 700 : 400,
                                                    color: selectedSize === s ? 'red.main' : 'inherit',
                                                    bgcolor: selectedSize === s ? '#FFF0F0' : '#fff',
                                                    transition: 'all 0.15s ease',
                                                    '&:hover': { border: '2px solid', borderColor: 'red.main', bgcolor: '#FFF0F0' },
                                                }}
                                            >
                                                {s}
                                            </Box>
                                        ))}
                                    </Box>
                                </Box> */}

                                {/* Quantity */}
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }} my={2.5}>
                                    <Typography variant="body2" fontWeight={600}>Qty</Typography>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            border: '1px solid #D9D9D9',
                                            borderRadius: '8px',
                                            overflow: 'hidden',
                                        }}
                                    >
                                        <IconButton
                                            id="qty-decrease-btn"
                                            size="small"
                                            onClick={() => handleQtyChange(-1)}
                                            sx={{ borderRadius: 0, bgcolor: 'red.main', color: '#fff', '&:hover': { bgcolor: '#b01030' }, px: '10px', py: '6px' }}
                                        >
                                            <RemoveIcon fontSize="small" />
                                        </IconButton>
                                        <Typography variant="body2" sx={{ px: '1.2rem', fontWeight: 600, minWidth: '2rem', textAlign: 'center' }}>
                                            {qty}
                                        </Typography>
                                        <IconButton
                                            id="qty-increase-btn"
                                            size="small"
                                            onClick={() => handleQtyChange(1)}
                                            sx={{ borderRadius: 0, bgcolor: 'red.main', color: '#fff', '&:hover': { bgcolor: '#b01030' }, px: '10px', py: '6px' }}
                                        >
                                            <AddIcon fontSize="small" />
                                        </IconButton>

                                    </Box>
                                </Box>

                                {/* CTA Buttons */}
                                <Box sx={{ display: 'flex', gap: 1.5, my: '0.5rem', flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
                                    <Button
                                        id="purchase-now-btn"
                                        variant="contained"
                                        onClick={handlePurchaseNow}
                                        sx={{
                                            flex: 1,
                                            minWidth: { xs: '100%', sm: 'auto' },
                                            bgcolor: 'red.main',
                                            borderRadius: '8px',
                                            color: '#fff',
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            fontSize: { xs: '14px', sm: '15px' },
                                            py: '10px',
                                            '&:hover': { boxShadow: '0 4px 12px rgba(220,20,60,0.25)', transform: 'translateY(-1px)' },
                                            transition: 'all 0.2s ease',
                                        }}
                                    >
                                        Purchase Now
                                    </Button>
                                    <Button
                                        id="add-to-cart-btn"
                                        variant="outlined"
                                        onClick={isInCart ? () => navigate('/Cartpage', { state: { sourcePath, sourceName } }) : handleAddToCart}
                                        sx={{
                                            flex: 1,
                                            minWidth: { xs: '100%', sm: 'auto' },
                                            borderRadius: '8px',
                                            borderColor: 'red.main',
                                            color: 'red.main',
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            fontSize: { xs: '14px', sm: '15px' },
                                            py: '10px',
                                            '&:hover': { borderColor: '#b01030', bgcolor: '#FFF0F0', transform: 'translateY(-1px)' },
                                            '&.Mui-disabled': { borderColor: '#ccc', color: '#999' },
                                            transition: 'all 0.2s ease',
                                        }}
                                    >
                                        {isInCart ? "View Cart" : "Add To Cart"}
                                    </Button>
                                </Box>

                                {/* Share row */}
                                <Box sx={{ display: 'flex', alignItems: 'center', mt: '1rem' }}>
                                    <Typography variant="caption" color="grey.500">Share this product :</Typography>
                                    <Box>
                                        <IconButton size="small" sx={{ p: 0.7 }} onClick={() => window.open(facebookUrl, '_blank')}>
                                            <FacebookIcon sx={{ fontSize: '18px', color: '#000' }} />
                                        </IconButton>
                                        <IconButton size="small" sx={{ p: 0.7 }} onClick={() => window.open(twitterUrl, '_blank')}>
                                            <XIcon sx={{ fontSize: '17px', color: '#000' }} />
                                        </IconButton>
                                        <IconButton size="small" sx={{ p: 0.7 }} onClick={() => window.open(instagramUrl, '_blank')}>
                                            <InstagramIcon sx={{ fontSize: '18px', color: '#000' }} />
                                        </IconButton>
                                        <IconButton size="small" sx={{ p: 0.7 }} onClick={() => window.open(whatsappUrl, '_blank')}>
                                            <WhatsAppIcon sx={{ fontSize: '18px', color: '#000' }} />
                                        </IconButton>
                                    </Box>
                                </Box>

                            </Box>
                        </Grid2>
                    </Grid2>
                </Box>

                {/* ── Tabs Section ── */}
                <Box
                    sx={{
                        border: '1px solid #E7E7E7',
                        borderRadius: '12px',
                        p: { xs: '1rem', md: '1.5rem' },
                        mb: '1.5rem',
                    }}
                >
                    {/* Tab headers */}
                    <Box sx={{ mb: 1 }}>
                        <Tabs
                            value={activeTab}
                            onChange={(_, v) => setActiveTab(v)}
                            variant="scrollable"
                            scrollButtons="auto"
                            TabIndicatorProps={{ style: { display: 'none' } }}
                            sx={{
                                minHeight: 'unset',
                                '& .MuiTab-root': {
                                    textTransform: 'none',
                                    fontWeight: 500,
                                    fontSize: '14px',
                                    minHeight: 'unset',
                                    py: '8px',
                                    px: '18px',
                                    mr: '6px',
                                    borderRadius: '8px',
                                    border: '1px solid #D9D9D9',
                                    color: '#444',
                                    transition: 'all 0.2s ease',
                                },
                                '& .Mui-selected': {
                                    bgcolor: '#DC143C !important',
                                    color: '#fff !important',
                                    border: '1px solid !important', borderColor: '#DC143C !important',
                                    fontWeight: 700,
                                },
                            }}
                        >
                            <Tab label="Product Description" id="product-tab-0" aria-controls="product-tab-panel-0" />
                            <Tab label="Contents In The Box" id="product-tab-1" aria-controls="product-tab-panel-1" />
                            <Tab label="Reviews" id="product-tab-2" aria-controls="product-tab-panel-2" />
                            <Tab label="Comments" id="product-tab-3" aria-controls="product-tab-panel-3" />
                        </Tabs>
                    </Box>

                    {/* Product Description */}
                    <TabPanel value={activeTab} index={0}>
                        {descriptionHTML ? (
                            <Box
                                sx={{ lineHeight: 1.8, color: 'grey.600', fontSize: '14px', '& p': { margin: '0 0 10px 0' }, '& table': { display: 'none' } }}
                                dangerouslySetInnerHTML={{ __html: descriptionHTML }}
                            />
                        ) : (
                            <Typography variant="body2" color="grey.600" sx={{ lineHeight: 1.8 }}>
                                No description available for this product.
                            </Typography>
                        )}
                    </TabPanel>

                    {/* Contents In The Box */}
                    <TabPanel value={activeTab} index={1}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {contentsList.length > 0 ? (
                                <Typography variant="body2" sx={{ lineHeight: 1.8, color: 'grey.700' }}>
                                    {contentsList.join(', ')}
                                </Typography>
                            ) : (
                                <Typography variant="body2" color="grey.500">
                                    No content information available.
                                </Typography>
                            )}
                        </Box>
                    </TabPanel>

                    {/* Reviews */}
                    <TabPanel value={activeTab} index={2}>
                        <Box>
                            <Typography variant="body2" color="grey.500">No reviews yet.</Typography>
                        </Box>
                    </TabPanel>

                    {/* Comments */}
                    <TabPanel value={activeTab} index={3}>
                        <Typography variant="body2" color="grey.500">No comments yet. Be the first to leave a comment!</Typography>
                    </TabPanel>

                    {/* ── Similar Products ── */}
                    {similarProducts.length > 0 && (
                        <>
                            <CustomDivider size="2rem" />

                            <Typography variant="body1" fontWeight={600} mb={1.5}>
                                Similar Products
                            </Typography>

                            <Grid2 container spacing="1rem">
                                {similarProducts.slice(0, 4).map((item, idx) => (
                                    <Grid2 key={idx} size={{ xs: 6, sm: 4, md: 3 }}>
                                        <SkillBoxProductCard item={item} />
                                    </Grid2>
                                ))}
                            </Grid2>
                        </>
                    )}
                </Box>

            </Box>

            {/* ── Snackbar ── */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={2500}
                onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
                    severity={snackbar.severity || "success"}
                    variant="filled"
                    sx={{ borderRadius: '10px', fontWeight: 500 }}
                    action={
                        snackbar.showAction !== false ? (
                            <Button
                                size="small"
                                variant='body2'
                                sx={{ color: '#fff', fontWeight: 500, textTransform: 'none' }}
                                onClick={() => navigate('/Cartpage', { state: { sourcePath, sourceName } })}
                            >
                                View Cart
                            </Button>
                        ) : null
                    }
                >
                    {snackbar.msg}
                </Alert>
            </Snackbar>
        </>
    );
};

export default SkillBoxProductDetail;

